import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/app/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Users, Factory } from "lucide-react";
import { jenisOf } from "@/components/app/PabrikSwitcher";

const JENIS_OPTS = [
  { id: "tungku", label: "Arang Kayu Tungku (Kayu Masuk → Bakar → Bongkar)" },
  { id: "reseller", label: "Reseller (beli arang jadi dari pabrik lain)" },
  { id: "briket", label: "Briket (bahan dari pabrik lain → cetak → jemur)" },
];
const empty = { nama: "", jenis: "reseller", produk_awal: "", aktif: true };

const PabrikCard = ({ p, onToggle, onEdit, onDelete }) => {
  const J = jenisOf(p.jenis);
  return (
    <div className={`card-flat p-4 flex flex-col gap-3 transition-opacity duration-150 ${p.aktif ? "" : "opacity-60"}`} data-testid={`sa-pabrik-${p.id}`}>
      <div className="flex items-start gap-3">
        <span className="h-9 w-9 rounded-sm flex items-center justify-center shrink-0" style={{ background: `${J.color}22` }}><J.icon size={18} style={{ color: J.color }}/></span>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-slate-900 truncate">{p.nama}</div>
          <div className="text-[11px] text-slate-500">{J.label}{p.produk_awal ? ` · produk: ${p.produk_awal}` : ""}</div>
        </div>
        <Switch checked={!!p.aktif} onCheckedChange={(v) => onToggle(p, v)} data-testid={`sa-pabrik-aktif-${p.id}`}/>
      </div>
      <div className="flex items-center justify-between text-xs">
        <span className={p.aktif ? "text-emerald-700" : "text-slate-500"} data-testid={`sa-pabrik-status-${p.id}`}>{p.aktif ? "Aktif" : "Nonaktif — tidak bisa diakses"} · {p.jumlah_data} data</span>
        <div className="flex gap-1">
          <button onClick={() => onEdit(p)} className="p-1.5 rounded-sm hover:bg-slate-100" data-testid={`sa-pabrik-edit-${p.id}`}><Pencil size={14}/></button>
          <button onClick={() => onDelete(p)} className="p-1.5 rounded-sm hover:bg-rose-50" data-testid={`sa-pabrik-del-${p.id}`}><Trash2 size={14} className="text-rose-600"/></button>
        </div>
      </div>
    </div>
  );
};

const AksesTable = ({ users, pabrik, onSave }) => (
  <div className="card-flat overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full text-xs md:text-sm min-w-[560px]" data-testid="sa-akses-table">
        <thead><tr className="bg-slate-50 border-b border-slate-200 text-left text-[11px] uppercase tracking-wide text-slate-500">
          <th className="p-3">User</th><th className="p-3 text-center">Semua</th>
          {pabrik.map((p) => <th key={p.id} className="p-3 text-center whitespace-nowrap">{p.nama.replace(/^Pabrik\s+/i, "")}</th>)}</tr></thead>
        <tbody>
          {users.length === 0 && <tr><td colSpan={pabrik.length + 2} className="text-center py-8 text-slate-400">Belum ada user di workspace ini.</td></tr>}
          {users.map((u) => {
            const all = !u.pabrik_access || u.pabrik_access.length === 0;
            const set = (ids) => onSave(u, ids);
            return (
              <tr key={u.id} className="border-b border-slate-100" data-testid={`sa-akses-row-${u.id}`}>
                <td className="p-3"><div className="font-medium text-slate-800">{u.name}</div><div className="text-[11px] text-slate-500">{u.email} · {u.role}</div></td>
                <td className="p-3 text-center"><Checkbox checked={all} onCheckedChange={(v) => set(v ? null : [pabrik[0]?.id])} data-testid={`sa-akses-all-${u.id}`}/></td>
                {pabrik.map((p) => {
                  const on = all || u.pabrik_access.includes(p.id);
                  return <td key={p.id} className="p-3 text-center"><Checkbox checked={on} disabled={all}
                    onCheckedChange={(v) => set(v ? [...u.pabrik_access, p.id] : u.pabrik_access.filter((x) => x !== p.id))} data-testid={`sa-akses-${u.id}-${p.id}`}/></td>;
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
);

export default function SuperAdminPabrik() {
  const [wsList, setWsList] = useState([]);
  const [ws, setWs] = useState("");
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(empty);

  useEffect(() => { api.get("/superadmin/arang-workspaces").then((r) => { setWsList(r.data); if (r.data[0]) setWs(r.data[0].id); }).catch((e) => toast.error(formatApiError(e))); }, []);
  const load = async () => {
    if (!ws) return;
    try {
      const [a, b] = await Promise.all([api.get("/superadmin/pabrik", { params: { workspace_id: ws } }), api.get("/superadmin/pabrik-users", { params: { workspace_id: ws } })]);
      setItems(a.data); setUsers(b.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [ws]); // eslint-disable-line react-hooks/exhaustive-deps

  const put = async (p, patch, msg) => {
    try { await api.put(`/superadmin/pabrik/${p.id}`, { nama: p.nama, jenis: p.jenis, produk_awal: p.produk_awal || "", aktif: p.aktif, ...patch }); toast.success(msg); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const onToggle = (p, v) => put(p, { aktif: v }, v ? `${p.nama} diaktifkan` : `${p.nama} dinonaktifkan`);
  const onDelete = async (p) => {
    if (!window.confirm(`Hapus ${p.nama}? Hanya bisa bila belum ada data.`)) return;
    try { await api.delete(`/superadmin/pabrik/${p.id}`); toast.success("Pabrik dihapus"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const openNew = () => { setEditId(null); setForm(empty); setOpen(true); };
  const onEdit = (p) => { setEditId(p.id); setForm({ nama: p.nama, jenis: p.jenis, produk_awal: p.produk_awal || "", aktif: p.aktif }); setOpen(true); };
  const save = async () => {
    if (!form.nama.trim()) { toast.error("Nama pabrik wajib diisi"); return; }
    try {
      if (editId) await api.put(`/superadmin/pabrik/${editId}`, form);
      else await api.post("/superadmin/pabrik", { ...form, workspace_id: ws });
      toast.success("Pabrik tersimpan"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const saveAkses = async (u, ids) => {
    try { await api.put(`/superadmin/pabrik-users/${u.id}`, { pabrik_access: ids }); toast.success("Akses diperbarui"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (<>
    <PageHeader title="Multi Pabrik (Bisnis Arang)" subtitle="Tambah, ubah, hapus, aktif/nonaktifkan pabrik per workspace, dan atur pabrik yang boleh diakses tiap user."
      actions={<>
        <Select value={ws} onValueChange={setWs}>
          <SelectTrigger className="w-full sm:w-64" data-testid="sa-pabrik-ws"><SelectValue placeholder="Pilih workspace arang"/></SelectTrigger>
          <SelectContent>{wsList.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
        </Select>
        <Button onClick={openNew} disabled={!ws} className="brand-btn rounded-sm" data-testid="sa-pabrik-add"><Plus size={14} className="mr-1"/>Tambah Pabrik</Button>
      </>}/>
    <div className="p-4 md:p-8 space-y-8">
      {wsList.length === 0 && <div className="card-flat p-6 text-sm text-slate-500">Belum ada workspace Bisnis Pabrik Arang.</div>}
      <section className="space-y-3">
        <h3 className="flex items-center gap-2 text-sm md:text-base font-semibold text-slate-900"><Factory size={16}/>Daftar Pabrik</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 md:gap-4">
          {items.map((p) => <PabrikCard key={p.id} p={p} onToggle={onToggle} onEdit={onEdit} onDelete={onDelete}/>)}
        </div>
      </section>
      <section className="space-y-3">
        <h3 className="flex items-center gap-2 text-sm md:text-base font-semibold text-slate-900"><Users size={16}/>Akses Pabrik per User</h3>
        <p className="text-xs text-slate-500">"Semua" = boleh pindah ke semua pabrik aktif + lihat laporan Gabungan.</p>
        <AksesTable users={users} pabrik={items} onSave={saveAkses}/>
      </section>
    </div>

    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg" data-testid="sa-pabrik-dialog">
        <DialogHeader><DialogTitle>{editId ? "Ubah" : "Tambah"} Pabrik</DialogTitle></DialogHeader>
        <div className="grid gap-4">
          <div className="space-y-1.5"><Label>Nama Pabrik <span className="text-rose-500">*</span></Label><Input value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} data-testid="sa-pabrik-nama"/></div>
          <div className="space-y-1.5"><Label>Jenis</Label>
            <Select value={form.jenis} onValueChange={(v) => setForm({ ...form, jenis: v })}>
              <SelectTrigger data-testid="sa-pabrik-jenis"><SelectValue/></SelectTrigger>
              <SelectContent>{JENIS_OPTS.map((j) => <SelectItem key={j.id} value={j.id}>{j.label}</SelectItem>)}</SelectContent>
            </Select>
            {editId && <div className="text-[11px] text-slate-500">Jenis hanya bisa diubah bila pabrik belum punya data.</div>}
          </div>
          {form.jenis === "reseller" && <div className="space-y-1.5"><Label>Produk Awal</Label><Input value={form.produk_awal} placeholder="mis. Arang Kayu Pendam" onChange={(e) => setForm({ ...form, produk_awal: e.target.value })} data-testid="sa-pabrik-produk"/></div>}
          <label className="flex items-center gap-2 text-sm"><Switch checked={form.aktif} onCheckedChange={(v) => setForm({ ...form, aktif: v })} data-testid="sa-pabrik-form-aktif"/> Aktif</label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Batal</Button>
          <Button onClick={save} className="brand-btn rounded-sm" data-testid="sa-pabrik-save">Simpan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>);
}
